import { ArrowBeakIcon } from '@hedepy/icons/refactor/ArrowBeak'
import { MinusIcon } from '@hedepy/icons/refactor/Minus'
import { PlusIcon } from '@hedepy/icons/refactor/Plus'
import { cva } from 'class-variance-authority'
import { Accordion as AccordionPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/legacy-src/lib/utils'

import { Text, type TextProps } from '@/src/domains/design-system/components/Text'

import { AccordionSize, AccordionVariant } from './Accordion.constants'

export * from './Accordion.constants'

export type AccordionProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>

export const Accordion = AccordionPrimitive.Root

interface AccordionVariantConfig {
  variant?: `${AccordionVariant}` | null
  size?: `${AccordionSize}` | null
}

const AccordionItemContext = React.createContext<AccordionVariantConfig>({})

const accordionItemVariants = cva('overflow-hidden rounded-xl transition-colors duration-200 ease-out', {
  variants: {
    variant: {
      [`${AccordionVariant.PINK}`]: 'bg-pink-500 data-[state=open]:bg-petrol-500',
      [`${AccordionVariant.SAND}`]: 'bg-sand-400 data-[state=open]:bg-white-500',
      [`${AccordionVariant.PLAIN}`]: 'rounded-none bg-transparent',
    },
  },
  defaultVariants: {
    variant: AccordionVariant.PINK,
  },
})

export interface AccordionItemProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>,
    AccordionVariantConfig {}

export const AccordionItem = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Item>, AccordionItemProps>(
  ({ className, variant, size, ...props }, ref) => (
    <AccordionItemContext.Provider value={{ variant, size }}>
      <AccordionPrimitive.Item ref={ref} className={cn(accordionItemVariants({ variant }), className)} {...props} />
    </AccordionItemContext.Provider>
  ),
)
AccordionItem.displayName = AccordionPrimitive.Item.displayName

const accordionTriggerVariants = cva(
  'group flex w-full cursor-pointer items-center border-0 bg-transparent text-left transition-[color,padding] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
  {
    variants: {
      size: {
        [`${AccordionSize.L}`]: '',
        [`${AccordionSize.M}`]: '',
        [`${AccordionSize.S}`]: '',
      },
      variant: {
        [`${AccordionVariant.PINK}`]:
          'text-petrol-500 focus-visible:ring-petrol-500 data-[state=open]:text-pink-500 data-[state=open]:focus-visible:ring-pink-500',
        [`${AccordionVariant.SAND}`]: 'text-petrol-500 focus-visible:ring-petrol-500',
        [`${AccordionVariant.PLAIN}`]:
          'border-b-2 border-solid border-sand-600 text-petrol-500 focus-visible:ring-petrol-500',
      },
    },
    compoundVariants: [
      {
        variant: [AccordionVariant.PINK, AccordionVariant.SAND],
        size: AccordionSize.L,
        class: 'gap-6 p-6 data-[state=open]:pb-3',
      },
      {
        variant: [AccordionVariant.PINK, AccordionVariant.SAND],
        size: AccordionSize.M,
        class: 'gap-5 p-5 data-[state=open]:pb-3',
      },
      {
        variant: [AccordionVariant.PINK, AccordionVariant.SAND],
        size: AccordionSize.S,
        class: 'gap-4 p-4 data-[state=open]:pb-3',
      },
      { variant: AccordionVariant.PLAIN, class: 'gap-5 p-0 pb-5' },
    ],
    defaultVariants: {
      size: AccordionSize.L,
      variant: AccordionVariant.PINK,
    },
  },
)

const PLAIN_TEXT_VARIANT: TextProps['variant'] = 'base'

const triggerTextVariantBySize: Record<AccordionSize, TextProps['variant']> = {
  [AccordionSize.L]: 'lg',
  [AccordionSize.M]: 'base',
  [AccordionSize.S]: 'sm',
}

const accordionTriggerIconVariants = cva('shrink-0', {
  variants: {
    variant: {
      [`${AccordionVariant.PINK}`]: '',
      [`${AccordionVariant.SAND}`]: 'text-peach-500',
      [`${AccordionVariant.PLAIN}`]:
        'text-peach-500 transition-transform duration-200 ease-out group-data-[state=open]:rotate-180',
    },
  },
  defaultVariants: {
    variant: AccordionVariant.PINK,
  },
})

export interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>,
    AccordionVariantConfig {}

export const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, children, size, variant, ...props }, ref) => {
  const itemContext = React.useContext(AccordionItemContext)
  const resolvedVariant = variant ?? itemContext.variant ?? AccordionVariant.PINK
  const resolvedSize = size ?? itemContext.size ?? AccordionSize.L
  const iconSize = resolvedVariant !== AccordionVariant.PLAIN && resolvedSize === AccordionSize.S ? 16 : 24
  const iconClassName = accordionTriggerIconVariants({ variant: resolvedVariant })

  return (
    <AccordionPrimitive.Header className="m-0 flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(accordionTriggerVariants({ size: resolvedSize, variant: resolvedVariant }), className)}
        {...props}
      >
        {resolvedVariant === AccordionVariant.PLAIN ? (
          <ArrowBeakIcon aria-hidden size={iconSize} className={iconClassName} />
        ) : (
          <>
            <PlusIcon aria-hidden size={iconSize} className={cn(iconClassName, 'group-data-[state=open]:hidden')} />
            <MinusIcon aria-hidden size={iconSize} className={cn(iconClassName, 'group-data-[state=closed]:hidden')} />
          </>
        )}
        <Text
          element="span"
          variant={
            resolvedVariant === AccordionVariant.PLAIN ? PLAIN_TEXT_VARIANT : triggerTextVariantBySize[resolvedSize]
          }
          weight="bold"
        >
          {children}
        </Text>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
})
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const accordionContentVariants = cva('', {
  variants: {
    size: {
      [`${AccordionSize.L}`]: '',
      [`${AccordionSize.M}`]: '',
      [`${AccordionSize.S}`]: '',
    },
    variant: {
      [`${AccordionVariant.PINK}`]: 'text-white-500',
      [`${AccordionVariant.SAND}`]: 'text-petrol-500',
      [`${AccordionVariant.PLAIN}`]: 'text-petrol-500',
    },
  },
  compoundVariants: [
    {
      variant: [AccordionVariant.PINK, AccordionVariant.SAND],
      size: AccordionSize.L,
      class: 'pb-6 pl-[4.5rem] pr-6',
    },
    {
      variant: [AccordionVariant.PINK, AccordionVariant.SAND],
      size: AccordionSize.M,
      class: 'pb-5 pl-16 pr-5',
    },
    {
      variant: [AccordionVariant.PINK, AccordionVariant.SAND],
      size: AccordionSize.S,
      class: 'pb-4 pl-12 pr-4',
    },
    { variant: AccordionVariant.PLAIN, class: 'pt-6' },
  ],
  defaultVariants: {
    size: AccordionSize.L,
    variant: AccordionVariant.PINK,
  },
})

const contentTextVariantBySize: Record<AccordionSize, TextProps['variant']> = {
  [AccordionSize.L]: 'base',
  [AccordionSize.M]: 'base',
  [AccordionSize.S]: 'sm',
}

export interface AccordionContentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>,
    AccordionVariantConfig {}

export const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, children, size, variant, ...props }, ref) => {
  const itemContext = React.useContext(AccordionItemContext)
  const resolvedVariant = variant ?? itemContext.variant ?? AccordionVariant.PINK
  const resolvedSize = size ?? itemContext.size ?? AccordionSize.L

  return (
    <AccordionPrimitive.Content
      ref={ref}
      className={cn(
        'overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        className,
      )}
      {...props}
    >
      <Text
        element="div"
        variant={
          resolvedVariant === AccordionVariant.PLAIN ? PLAIN_TEXT_VARIANT : contentTextVariantBySize[resolvedSize]
        }
        className={accordionContentVariants({ size: resolvedSize, variant: resolvedVariant })}
      >
        {children}
      </Text>
    </AccordionPrimitive.Content>
  )
})
AccordionContent.displayName = AccordionPrimitive.Content.displayName
